import { useEffect, useState } from 'react';
import { Settings as SettingsIcon, Sparkles, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useUserStore } from '@/stores/userStore';
import { useCompactMode } from '@/hooks/useCompactMode';
import { useChatFontSize, FONT_SIZE_OPTIONS, ChatFontSize } from '@/hooks/useChatFontSize';
import { useDocumentFontSize, DOC_FONT_SIZE_OPTIONS, DocumentFontSize } from '@/hooks/useDocumentFontSize';
import { useResourcesFontSize, RESOURCES_FONT_SIZE_OPTIONS, ResourcesFontSize } from '@/hooks/useResourcesFontSize';
import { useChatLanguage, LANGUAGE_OPTIONS, ChatLanguage } from '@/hooks/useChatLanguage';

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const userSettings = useUserStore(state => state.userSettings);
  const updateUserSettings = useUserStore(state => state.updateUserSettings);
  const loadUserSettingsFromBackend = useUserStore(state => state.loadUserSettingsFromBackend);
  const [isLoading, setIsLoading] = useState(false);
  const { compactMode, setCompactMode } = useCompactMode();
  const { chatFontSize, setChatFontSize } = useChatFontSize();
  const { documentFontSize, setDocumentFontSize } = useDocumentFontSize();
  const { resourcesFontSize, setResourcesFontSize } = useResourcesFontSize();
  const { chatLanguage, setChatLanguage } = useChatLanguage();

  // Load settings from backend when modal opens
  useEffect(() => {
    if (open) {
      console.log('[SettingsModal] Modal opened, loading settings from backend');
      setIsLoading(true);
      loadUserSettingsFromBackend().finally(() => {
        setIsLoading(false);
      });
    }
  }, [open, loadUserSettingsFromBackend]);

  const showWizardOnStartup = userSettings.showWizardOnStartup !== false; // Default to true if not set

  const handleToggleWizardOnStartup = (checked: boolean) => {
    updateUserSettings('showWizardOnStartup', checked);
  };

  const handleShowWizardNow = () => {
    // Close the settings modal first
    onOpenChange(false);

    // Wait for modal to fully close before showing wizard
    setTimeout(() => {
      // Reset onboarding and trigger the wizard event
      updateUserSettings('hasCompletedOnboarding', false);
      window.dispatchEvent(new Event('open-onboarding-wizard'));
    }, 300); // Wait for dialog close animation
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-gray-700" />
            <DialogTitle>Settings</DialogTitle>
          </div>
          <DialogDescription>
            Manage your Patent Loop preferences
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <span className="ml-3 text-sm text-gray-600">Loading settings...</span>
            </div>
          ) : (
            <>
              {/* Onboarding Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Onboarding</h3>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="wizard-startup" className="text-sm font-medium">
                      Show wizard on startup
                    </Label>
                    <p className="text-xs text-gray-500">
                      Display the onboarding wizard each time you log in (takes effect on next login)
                    </p>
                  </div>
                  <Switch
                    id="wizard-startup"
                    checked={showWizardOnStartup}
                    onCheckedChange={handleToggleWizardOnStartup}
                    disabled={isLoading}
                  />
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleShowWizardNow}
                    variant="outline"
                    className="w-full gap-2"
                    disabled={isLoading}
                  >
                    <Sparkles className="w-4 h-4" />
                    Show Wizard Now
                  </Button>
                </div>
              </div>

              {/* Display Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Display</h3>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="compact-mode" className="text-sm font-medium">
                      Compact Mode
                    </Label>
                    <p className="text-xs text-gray-500">
                      Reduce spacing and font sizes throughout the application for a denser layout
                    </p>
                  </div>
                  <Switch
                    id="compact-mode"
                    checked={compactMode}
                    onCheckedChange={setCompactMode}
                    disabled={isLoading}
                  />
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="chat-language" className="text-sm font-medium">
                      Chat Response Language
                    </Label>
                    <p className="text-xs text-gray-500">
                      Language for AI chat responses
                    </p>
                  </div>
                  <Select
                    value={chatLanguage}
                    onValueChange={(value) => setChatLanguage(value as ChatLanguage)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {LANGUAGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}{option.nativeName ? ` (${option.nativeName})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="chat-font-size" className="text-sm font-medium">
                      Chat Font Size
                    </Label>
                    <p className="text-xs text-gray-500">
                      Adjust the font size for chat messages
                    </p>
                  </div>
                  <Select
                    value={chatFontSize}
                    onValueChange={(value) => setChatFontSize(value as ChatFontSize)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span style={{ fontSize: option.size }}>{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="doc-font-size" className="text-sm font-medium">
                      Document Font Size
                    </Label>
                    <p className="text-xs text-gray-500">
                      Adjust the font size for document viewer content
                    </p>
                  </div>
                  <Select
                    value={documentFontSize}
                    onValueChange={(value) => setDocumentFontSize(value as DocumentFontSize)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_FONT_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span style={{ fontSize: option.size }}>{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="resources-font-size" className="text-sm font-medium">
                      Resources Font Size
                    </Label>
                    <p className="text-xs text-gray-500">
                      Adjust the font size for resources page content (MPEP, CFR, USC, etc.)
                    </p>
                  </div>
                  <Select
                    value={resourcesFontSize}
                    onValueChange={(value) => setResourcesFontSize(value as ResourcesFontSize)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCES_FONT_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span style={{ fontSize: option.size }}>{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
